import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import SyncIcon from "@mui/icons-material/Sync";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMissedIcon from "@mui/icons-material/CallMissed";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import BlockIcon from "@mui/icons-material/Block";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RingVolumeIcon from "@mui/icons-material/RingVolume";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import HistoryIcon from "@mui/icons-material/History";
import DialpadIcon from "@mui/icons-material/Dialpad";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import VoicemailIcon from "@mui/icons-material/Voicemail";
import CallEndIcon from "@mui/icons-material/CallEnd";
import CircleIcon from "@mui/icons-material/Circle";
import ContactsIcon from "@mui/icons-material/Contacts";
import { getRequestWithToken, postRequest } from "../../utils/ApiRequests";
import { GlobalContext } from "../../components/GlobalStateProvide/GlobalStateProvider";
import { useNavigate } from "react-router-dom";
import Unauthorized from "../../components/unauthorized/Unauthorized";

const statusConfig = {
  initiated: { label: "Initiating", icon: <RingVolumeIcon fontSize="small" /> },
  ringing: { label: "Ringing", icon: <RingVolumeIcon fontSize="small" /> },
  answered: { label: "Answered", icon: <CheckCircleIcon fontSize="small" /> },
  completed: { label: "Completed", icon: <CheckCircleIcon fontSize="small" /> },
  failed: { label: "Failed", icon: <ErrorIcon fontSize="small" /> },
  cancelled: { label: "Cancelled", icon: <CallMissedIcon fontSize="small" /> },
  missed: { label: "Missed", icon: <CallMissedIcon fontSize="small" /> },
};

const numberRows = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

const CallLogs = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { handleSnackBar } = useContext(GlobalContext);
  const navigate = useNavigate();
  const api = "../v3/admin";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [phone, setPhone] = useState("");
  const [calling, setCalling] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [fetchingRecording, setFetchingRecording] = useState(false);
  const [recordUrl, setRecordUrl] = useState(null);
  const [showDialpad, setShowDialpad] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ phone_number: "", name: "", email_id: "", marketing_opt_in: false });
  const [savingContact, setSavingContact] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockForm, setBlockForm] = useState({ phone_number: "", reason: "", notes: "" });
  const [savingBlock, setSavingBlock] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsList, setContactsList] = useState([]);
  const adminProfile = JSON.parse(localStorage.getItem("adminProfile") || "null");
  const resourceNames = JSON.parse(localStorage.getItem("resourceNames") || "[]");
  const isSuperAdmin = Boolean(adminProfile?.is_super_admin);
  const canUseCommunication =
    isSuperAdmin ||
    resourceNames.includes("Call Logs");

  const surface = {
    pageGlow: isDark
      ? `radial-gradient(circle at top right, ${alpha(theme.palette.primary.main, 0.24)}, transparent 42%), radial-gradient(circle at left top, ${alpha(theme.palette.secondary.main, 0.18)}, transparent 30%), linear-gradient(180deg, ${alpha("#0d0d0d", 0.96)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`
      : `radial-gradient(circle at top right, ${alpha(theme.palette.primary.main, 0.18)}, transparent 40%), radial-gradient(circle at left top, ${alpha(theme.palette.secondary.main, 0.1)}, transparent 28%), linear-gradient(180deg, #fffaf4 0%, ${theme.palette.background.default} 100%)`,
    panel: isDark ? alpha("#171717", 0.92) : alpha("#ffffff", 0.92),
    panelSoft: isDark ? alpha("#202020", 0.88) : alpha("#fff8ef", 0.9),
    panelMuted: isDark ? alpha("#252525", 0.85) : alpha("#f8f1e7", 0.95),
    border: alpha(theme.palette.divider, isDark ? 0.7 : 0.95),
    hero: isDark
      ? `linear-gradient(135deg, ${alpha("#1f1303", 0.96)} 0%, ${alpha("#33200d", 0.94)} 35%, ${alpha("#102218", 0.92)} 100%)`
      : "linear-gradient(135deg, #fff8ea 0%, #fff1da 42%, #edf8f0 100%)",
    accent: isDark
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.secondary.main, 0.22)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    textInverse: isDark ? "#fff3de" : "#3a2412",
    codeBg: isDark ? "#101010" : "#fffaf2",
    codeText: isDark ? "#9ef7b5" : "#245f36",
  };

  const getApiMessage = (err, fallback) =>
    err?.response?.data?.msg ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;

  const fetchLogs = useCallback(async (options = {}) => {
    const { showSuccess = false, successMessage = "Call logs refreshed", errorMessage = "Failed to fetch call logs" } = options;
    setLoading(true);
    try {
      const res = await getRequestWithToken(`${api}/call-logs`);
      if (res.data?.status === "Success") {
        setLogs(res.data.data);
        if (showSuccess) {
          handleSnackBar(true, successMessage, "success");
        }
      } else {
        handleSnackBar(true, res?.data?.msg || errorMessage, "error");
      }
    } catch (err) {
      console.error("Failed to fetch call logs", err);
      handleSnackBar(true, getApiMessage(err, errorMessage), "error");
    }
    setLoading(false);
  }, [api, handleSnackBar]);

  useEffect(() => {
    if (!canUseCommunication) {
      return;
    }
    fetchLogs();
  }, [canUseCommunication, fetchLogs]);

  if (!canUseCommunication) {
    return <Unauthorized />;
  }

  const handleCall = async () => {
    if (!phone || calling) return;
    setCalling(true);
    try {
      const res = await postRequest(`${api}/call-logs/initiate`, { to_number: phone });
      if (res.data?.status === "Success") {
        handleSnackBar(true, "Call initiated", "success");
        setPhone("");
        fetchLogs({ successMessage: "Call logs refreshed" });
      } else {
        handleSnackBar(true, res?.data?.msg || "Failed", "error");
      }
    } catch (err) {
      handleSnackBar(true, getApiMessage(err, "Failed to initiate call"), "error");
    }
    setCalling(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await postRequest(`${api}/call-logs/sync`, {});
      if (res.data?.status === "Success") {
        handleSnackBar(
          true,
          `Synced ${res.data.data?.synced || 0} new calls and refreshed ${res.data.data?.updated || 0} existing records`,
          "success"
        );
        fetchLogs({ successMessage: "Call logs refreshed" });
      } else {
        handleSnackBar(true, res?.data?.msg || "Sync completed", "info");
        fetchLogs({ successMessage: "Call logs refreshed" });
      }
    } catch (err) {
      handleSnackBar(true, getApiMessage(err, "Sync failed"), "error");
    }
    setSyncing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleCall();
  };

  const getLogNumber = useCallback(
    (log) =>
      log?.called_number ||
      log?.to_number ||
      log?.caller_number ||
      log?.from_number ||
      "",
    []
  );

  const openDetail = (log) => {
    setSelectedLog(log);
    setNotesText("");
    setRecordUrl(log.recording_url || null);
    setContactInfo(null);
    setDetailOpen(true);
  };

  const fetchContactInfo = useCallback(
    async (phoneNumber, options = {}) => {
      if (!phoneNumber) {
        setContactInfo(null);
        return;
      }

      const { silent = false } = options;
      setContactLoading(true);
      try {
        const res = await getRequestWithToken(`${api}/call-logs/contact?phone_number=${encodeURIComponent(phoneNumber)}`);
        if (res.data?.status === "Success") {
          setContactInfo(res.data.data);
        } else {
          setContactInfo({ contacts: [] });
          if (!silent) {
            handleSnackBar(true, res.data?.msg || "Failed to fetch contact", "error");
          }
        }
      } catch (err) {
        setContactInfo({ contacts: [] });
        if (!silent) {
          handleSnackBar(true, getApiMessage(err, "Failed to fetch contact"), "error");
        }
      }
      setContactLoading(false);
    },
    [api, handleSnackBar]
  );

  useEffect(() => {
    if (detailOpen && selectedLog) {
      fetchContactInfo(getLogNumber(selectedLog), { silent: true });
    }
  }, [detailOpen, selectedLog, fetchContactInfo, getLogNumber]);

  const handleGetRecording = async () => {
    if (!selectedLog) return;
    setFetchingRecording(true);
    try {
      const res = await getRequestWithToken(`${api}/call-logs/${selectedLog._id}/recording`);
      if (res.data?.status === "Success") {
        const url = res.data.data?.recording_url;
        if (url) {
          setRecordUrl(url);
          handleSnackBar(true, "Recording found", "success");
        } else {
          handleSnackBar(true, "No recording available yet", "info");
        }
      } else {
        handleSnackBar(true, res?.data?.msg || "Failed to fetch recording", "error");
      }
    } catch (err) {
      handleSnackBar(true, getApiMessage(err, "Failed to fetch recording"), "error");
    }
    setFetchingRecording(false);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    handleSnackBar(true, "Copied to clipboard", "success");
  };

  const handleKeypadPress = (digit) => {
    setPhone((prev) => prev + digit);
  };

  const handleBackspace = () => {
    setPhone((prev) => prev.slice(0, -1));
  };

  const openContactDialog = (log = selectedLog) => {
    const targetNumber = getLogNumber(log);
    if (!targetNumber) {
      handleSnackBar(true, "Phone number not available for this log", "error");
      return;
    }

    setContactForm({
      phone_number: targetNumber,
      name: "",
      email_id: "",
      marketing_opt_in: false,
    });
    setContactDialogOpen(true);
  };

  const handleCreateContact = async () => {
    if (!contactForm.phone_number) return;
    setSavingContact(true);
    try {
      const res = await postRequest(`${api}/call-logs/contact`, contactForm);
      if (res.data?.status === "Success") {
        handleSnackBar(true, res.data?.msg || "Contact saved", "success");
        setContactDialogOpen(false);
        fetchContactInfo(contactForm.phone_number, { silent: true });
      } else {
        handleSnackBar(true, res.data?.msg || "Failed to save contact", "error");
      }
    } catch (err) {
      handleSnackBar(true, getApiMessage(err, "Failed to save contact"), "error");
    }
    setSavingContact(false);
  };

  const openBlockDialog = (log = selectedLog) => {
    const targetNumber = getLogNumber(log);
    if (!targetNumber) {
      handleSnackBar(true, "Phone number not available for this log", "error");
      return;
    }

    setBlockForm({
      phone_number: targetNumber,
      reason: log?.blocked_reason || "",
      notes: log?.blocked_notes || "",
    });
    setBlockDialogOpen(true);
  };

  const handleBlockNumber = async () => {
    if (!blockForm.phone_number) return;
    setSavingBlock(true);
    try {
      const res = await postRequest(`${api}/call-logs/block`, blockForm);
      if (res.data?.status === "Success") {
        handleSnackBar(true, res.data?.msg || "Number blocked", "success");
        setBlockDialogOpen(false);
        setDetailOpen(false);
        fetchLogs({ successMessage: "Call logs refreshed" });
      } else {
        handleSnackBar(true, res.data?.msg || "Failed to block number", "error");
      }
    } catch (err) {
      handleSnackBar(true, getApiMessage(err, "Failed to block number"), "error");
    }
    setSavingBlock(false);
  };

  const openContactsDialog = async () => {
    if (!isSuperAdmin) {
      handleSnackBar(true, "Only super-admin can view all MyOperator contacts", "error");
      return;
    }

    setContactsDialogOpen(true);
    setContactsLoading(true);
    try {
      const res = await getRequestWithToken(`${api}/call-logs/contacts?limit=100`);
      if (res.data?.status === "Success") {
        setContactsList(res.data?.data?.contacts || []);
        handleSnackBar(true, "Fetched contacts from MyOperator", "success");
      } else {
        setContactsList([]);
        handleSnackBar(true, res.data?.msg || "Failed to fetch contacts", "error");
      }
    } catch (err) {
      setContactsList([]);
      handleSnackBar(true, getApiMessage(err, "Failed to fetch contacts"), "error");
    }
    setContactsLoading(false);
  };

  const formatDuration = (s) => {
    if (!s || s === 0) return "-";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const formatTime = (t) => {
    if (!t) return "-";
    const d = new Date(t);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const timeStr = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    const dateStr = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    if (isToday) return `Today ${timeStr}`;
    if (isYesterday) return `Yesterday ${timeStr}`;
    return `${dateStr} ${timeStr}`;
  };

  const getStatusTone = (status) => {
    if (status === "completed" || status === "answered") {
      return {
        chipBg: alpha("#1fa971", isDark ? 0.22 : 0.12),
        chipColor: isDark ? "#87f2c0" : "#0d6c48",
        glow: alpha("#1fa971", 0.22),
      };
    }
    if (status === "failed" || status === "cancelled" || status === "missed") {
      return {
        chipBg: alpha("#d64f4f", isDark ? 0.22 : 0.12),
        chipColor: isDark ? "#ffb0b0" : "#9d2727",
        glow: alpha("#d64f4f", 0.18),
      };
    }
    return {
      chipBg: alpha(theme.palette.primary.main, isDark ? 0.24 : 0.14),
      chipColor: isDark ? "#ffd18a" : "#9a5700",
      glow: alpha(theme.palette.primary.main, 0.2),
    };
  };

  const getCallTypeTone = (log) => {
    if (log.status === "failed" || log.status === "cancelled" || log.status === "missed") {
      return {
        bg: isDark
          ? "linear-gradient(135deg, rgba(109, 27, 27, 0.92) 0%, rgba(52, 14, 14, 0.96) 100%)"
          : "linear-gradient(135deg, #fff0ed 0%, #ffe1d7 100%)",
        avatar: "linear-gradient(135deg, #d65a5a 0%, #b43636 100%)",
        line: alpha("#d64f4f", 0.38),
      };
    }
    if (log.direction === "inbound") {
      return {
        bg: isDark
          ? "linear-gradient(135deg, rgba(23, 40, 74, 0.92) 0%, rgba(15, 24, 47, 0.96) 100%)"
          : "linear-gradient(135deg, #eef5ff 0%, #e3f0ff 100%)",
        avatar: "linear-gradient(135deg, #4b7bec 0%, #265fda 100%)",
        line: alpha("#4b7bec", 0.38),
      };
    }
    return {
      bg: isDark
        ? "linear-gradient(135deg, rgba(21, 61, 44, 0.92) 0%, rgba(13, 36, 26, 0.96) 100%)"
        : "linear-gradient(135deg, #effcf2 0%, #e0f8e7 100%)",
      avatar: "linear-gradient(135deg, #24a36a 0%, #0d7f4d 100%)",
      line: alpha("#24a36a", 0.36),
    };
  };

  const detailRows = selectedLog
    ? [
        { label: "From", value: selectedLog.from_number || selectedLog.caller_number },
        { label: "To", value: selectedLog.to_number || selectedLog.called_number },
        { label: "Source", value: selectedLog.call_source_label },
        { label: "Agent", value: selectedLog.agent_name || selectedLog.agent_number },
        { label: "Agent Number", value: selectedLog.agent_name ? selectedLog.agent_number : "" },
        { label: "Admin User", value: selectedLog.initiated_by_admin_name },
        { label: "Disposition", value: selectedLog.disposition },
        { label: "Call Type", value: selectedLog.call_type },
        { label: "Session ID", value: selectedLog.session_id },
        { label: "Reference ID", value: selectedLog.reference_id },
        { label: "Call ID", value: selectedLog.unique_id },
      ].filter((row) => row.value)
    : [];

  const timelineRows = selectedLog
    ? [
        { label: "Initiated", time: selectedLog.initiated_at, icon: <RingVolumeIcon /> },
        { label: "Ringing", time: selectedLog.ringing_at, icon: <RingVolumeIcon /> },
        { label: "Answered", time: selectedLog.answered_at, icon: <CheckCircleIcon /> },
        { label: "Ended", time: selectedLog.ended_at, icon: <CallEndIcon /> },
      ].filter((row) => row.time)
    : [];

  const totalCalls = logs.length;
  const completedCalls = logs.filter((log) => log.status === "completed").length;
  const activeCalls = logs.filter((log) => ["ringing", "initiated", "answered"].includes(log.status)).length;
  const recordedCalls = logs.filter((log) => log.recording_url || log.recording_file).length;
  const callInProgress = activeCalls > 0;

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        borderRadius: { xs: 3, md: 4 },
        background: surface.pageGlow,
        border: `1px solid ${surface.border}`,
        overflow: "hidden",
        boxShadow: isDark ? "0 26px 60px rgba(0, 0, 0, 0.35)" : "0 24px 60px rgba(114, 82, 35, 0.12)",
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            background: surface.hero,
            border: `1px solid ${surface.border}`,
            color: surface.textInverse,
            position: "relative",
            overflow: "hidden",
            mb: 2.5,
            "&::before": {
              content: '""',
              position: "absolute",
              width: 240,
              height: 240,
              right: -80,
              top: -120,
              borderRadius: "50%",
              background: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
            },
            "&::after": {
              content: '""',
              position: "absolute",
              width: 180,
              height: 180,
              left: -60,
              bottom: -100,
              borderRadius: "50%",
              background: alpha(theme.palette.secondary.main, isDark ? 0.12 : 0.09),
            },
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} lg={7}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      color: isDark ? "#111" : "#fff",
                      boxShadow: `0 14px 28px ${alpha(theme.palette.primary.main, 0.24)}`,
                    }}
                  >
                    <PhoneInTalkIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.72 }}>
                      Communication Console
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -1, lineHeight: 1.05 }}>
                      Call Center
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.8, maxWidth: 520, opacity: 0.82 }}>
                      A cleaner live view for outbound calling, activity tracking, and fast call-detail review.
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  justifyContent={{ lg: "flex-end" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => fetchLogs({ showSuccess: true })}
                    startIcon={<RefreshIcon />}
                    sx={{
                      color: surface.textInverse,
                      borderColor: alpha("#ffffff", isDark ? 0.16 : 0.3),
                      bgcolor: alpha("#ffffff", isDark ? 0.04 : 0.35),
                      backdropFilter: "blur(14px)",
                      "&:hover": {
                        borderColor: alpha("#ffffff", 0.44),
                        bgcolor: alpha("#ffffff", isDark ? 0.08 : 0.44),
                      },
                    }}
                  >
                    Refresh
                  </Button>
                  {isSuperAdmin && (
                    <Button
                      variant="outlined"
                      onClick={openContactsDialog}
                      startIcon={<ContactsIcon />}
                      sx={{
                        color: surface.textInverse,
                        borderColor: alpha("#ffffff", isDark ? 0.16 : 0.3),
                        bgcolor: alpha("#ffffff", isDark ? 0.04 : 0.2),
                        "&:hover": {
                          borderColor: alpha("#ffffff", 0.44),
                          bgcolor: alpha("#ffffff", isDark ? 0.08 : 0.32),
                        },
                      }}
                    >
                      Contacts
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleSync}
                    disabled={syncing}
                    startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                    sx={{
                      minWidth: 148,
                      color: isDark ? "#1b1306" : "#fff",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #ffb347 100%)`,
                      boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.28)}`,
                      "&:hover": {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark || theme.palette.primary.main} 0%, #ff9b21 100%)`,
                      },
                      "&.Mui-disabled": {
                        color: alpha("#ffffff", 0.64),
                        background: alpha("#ffffff", isDark ? 0.12 : 0.26),
                      },
                    }}
                  >
                    {syncing ? "Syncing..." : "Sync Logs"}
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              {[
                { label: "Total Calls", value: totalCalls, helper: "Across the current log feed" },
                { label: "Completed", value: completedCalls, helper: "Finished successfully" },
                { label: "Active", value: activeCalls, helper: "Live or recently connected" },
                { label: "Recordings", value: recordedCalls, helper: "Playback available" },
              ].map((item) => (
                <Grid item xs={6} md={3} key={item.label}>
                  <Box
                    sx={{
                      p: 1.6,
                      borderRadius: 3,
                      background: alpha("#ffffff", isDark ? 0.06 : 0.48),
                      border: `1px solid ${alpha("#ffffff", isDark ? 0.08 : 0.4)}`,
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.72, letterSpacing: 0.4 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ mt: 0.3, fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
                      {item.value}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5, display: "block", opacity: 0.64 }}>
                      {item.helper}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={2.5}>
          <Grid item xs={12} xl={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 4,
                background: surface.panel,
                border: `1px solid ${surface.border}`,
                backdropFilter: "blur(18px)",
              }}
            >
              <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
                      Quick Dial
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Start a new call
                    </Typography>
                  </Box>

                  <TextField
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter phone number"
                    size="medium"
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        background: surface.panelMuted,
                        fontSize: 20,
                        fontWeight: 700,
                        letterSpacing: 1,
                        fontFamily: "'Courier New', monospace",
                        "& fieldset": {
                          borderColor: surface.border,
                        },
                        "&:hover fieldset": {
                          borderColor: alpha(theme.palette.primary.main, 0.7),
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />

                  <Stack direction="row" spacing={1.25}>
                    <Tooltip title={showDialpad ? "Hide keypad" : "Show keypad"}>
                      <IconButton
                        onClick={() => setShowDialpad(!showDialpad)}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 3,
                          background: showDialpad ? surface.accent : surface.panelMuted,
                          color: showDialpad ? theme.palette.primary.main : theme.palette.text.secondary,
                          border: `1px solid ${surface.border}`,
                        }}
                      >
                        {showDialpad ? <KeyboardIcon /> : <DialpadIcon />}
                      </IconButton>
                    </Tooltip>

                    <Button
                      variant="contained"
                      onClick={handleCall}
                      disabled={!phone || calling}
                      fullWidth
                      startIcon={
                        calling ? <CircularProgress size={16} color="inherit" /> : <PhoneIcon />
                      }
                      sx={{
                        minHeight: 50,
                        borderRadius: 3,
                        fontWeight: 700,
                        background: "linear-gradient(135deg, #1bb56f 0%, #0e8f56 100%)",
                        boxShadow: "0 14px 28px rgba(22, 145, 87, 0.24)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #17a364 0%, #0a7f4c 100%)",
                        },
                        "&.Mui-disabled": {
                          background: isDark ? alpha("#ffffff", 0.1) : alpha("#111111", 0.08),
                          color: theme.palette.text.secondary,
                        },
                      }}
                    >
                      {calling ? "Calling..." : "Place Call"}
                    </Button>
                  </Stack>

                  {showDialpad && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        background: surface.panelSoft,
                        border: `1px solid ${surface.border}`,
                      }}
                    >
                      <Grid container spacing={1.1}>
                        {numberRows.flat().map((digit) => (
                          <Grid item xs={4} key={digit}>
                            <Button
                              onClick={() => handleKeypadPress(digit)}
                              fullWidth
                              sx={{
                                minWidth: 0,
                                minHeight: 54,
                                borderRadius: 3,
                                fontSize: 22,
                                fontWeight: 700,
                                background: isDark ? alpha("#ffffff", 0.04) : "#ffffff",
                                color: theme.palette.text.primary,
                                border: `1px solid ${surface.border}`,
                                "&:hover": {
                                  transform: "translateY(-1px)",
                                  background: surface.accent,
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              {digit}
                            </Button>
                          </Grid>
                        ))}
                        <Grid item xs={4}>
                          <Button
                            onClick={handleBackspace}
                            fullWidth
                            sx={{
                              minWidth: 0,
                              minHeight: 54,
                              borderRadius: 3,
                              fontSize: 18,
                              border: `1px solid ${surface.border}`,
                              color: theme.palette.text.secondary,
                              background: isDark ? alpha("#ffffff", 0.02) : alpha("#000000", 0.02),
                              "&:hover": {
                                color: theme.palette.error.main,
                                background: alpha(theme.palette.error.main, 0.08),
                              },
                            }}
                          >
                            ⌫
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      background: surface.panelSoft,
                      border: `1px solid ${surface.border}`,
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          background: callInProgress
                            ? "linear-gradient(135deg, #1bb56f 0%, #0e8f56 100%)"
                            : surface.accent,
                          color: callInProgress ? "#fff" : theme.palette.primary.main,
                        }}
                      >
                        <VoicemailIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                          {callInProgress ? "Call activity detected" : "No active calls"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {callInProgress
                            ? "Refresh the log if you want the latest status changes."
                            : "New calls and synced records will appear in the activity stream."}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} xl={8}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                background: surface.panel,
                border: `1px solid ${surface.border}`,
                backdropFilter: "blur(18px)",
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 }, "&:last-child": { pb: { xs: 2, md: 2.5 } } }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <HistoryIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        Call History
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Browse recent activity, inspect call outcomes, and open recordings from one place.
                    </Typography>
                  </Box>
                  <Chip
                    label={`${totalCalls} ${totalCalls === 1 ? "call" : "calls"}`}
                    sx={{
                      background: surface.accent,
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                {callInProgress && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      borderRadius: 3,
                      background: isDark
                        ? alpha("#1bb56f", 0.12)
                        : "linear-gradient(135deg, rgba(27,181,111,0.08) 0%, rgba(14,143,86,0.08) 100%)",
                      border: `1px solid ${alpha("#1bb56f", 0.24)}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                    }}
                  >
                    <FiberManualRecordIcon
                      sx={{
                        color: "#1bb56f",
                        fontSize: 14,
                        animation: "jy-call-pulse 1.2s ease-in-out infinite",
                        "@keyframes jy-call-pulse": {
                          "0%, 100%": { opacity: 1, transform: "scale(0.9)" },
                          "50%": { opacity: 0.45, transform: "scale(1.2)" },
                        },
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
                      Active call in progress
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => fetchLogs({ showSuccess: true })}
                      startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                    >
                      Refresh
                    </Button>
                  </Box>
                )}

                <Box sx={{ minHeight: 420 }}>
                  {loading ? (
                    <Box sx={{ py: 10, textAlign: "center" }}>
                      <CircularProgress />
                    </Box>
                  ) : logs.length === 0 ? (
                    <Box sx={{ py: 10, textAlign: "center" }}>
                      <Avatar
                        sx={{
                          width: 82,
                          height: 82,
                          mx: "auto",
                          mb: 2,
                          background: surface.accent,
                          color: theme.palette.primary.main,
                        }}
                      >
                        <PhoneInTalkIcon sx={{ fontSize: 36 }} />
                      </Avatar>
                      <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                        No calls yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 320, mx: "auto" }}>
                        Use the dialer to start a call or sync the latest records from MyOperator.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {logs.map((log) => {
                        const st = statusConfig[log.status] || statusConfig.initiated;
                        const displayNumber = getLogNumber(log) || "-";
                        const colors = getCallTypeTone(log);
                        const statusTone = getStatusTone(log.status);
                        const isActive = ["ringing", "initiated", "answered"].includes(log.status);

                        return (
                          <Card
                            key={log._id}
                            onClick={() => openDetail(log)}
                            elevation={0}
                            sx={{
                              cursor: "pointer",
                              borderRadius: 3,
                              background: colors.bg,
                              border: `1px solid ${isActive ? statusTone.glow : surface.border}`,
                              boxShadow: isActive
                                ? `0 14px 30px ${statusTone.glow}`
                                : isDark
                                  ? "0 10px 24px rgba(0, 0, 0, 0.18)"
                                  : "0 10px 24px rgba(93, 73, 42, 0.07)",
                              transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: isDark
                                  ? "0 16px 34px rgba(0, 0, 0, 0.24)"
                                  : "0 16px 34px rgba(93, 73, 42, 0.12)",
                              },
                            }}
                          >
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                              <Stack direction="row" spacing={1.6} alignItems="center">
                                <Avatar
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    background: colors.avatar,
                                    boxShadow: `0 12px 24px ${alpha("#000000", 0.14)}`,
                                  }}
                                >
                                  {log.direction === "inbound" ? <CallReceivedIcon /> : <CallMadeIcon />}
                                </Avatar>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={0.8}
                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                    justifyContent="space-between"
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 800,
                                        fontSize: 16,
                                        fontFamily: "'Courier New', monospace",
                                        letterSpacing: 0.4,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {displayNumber}
                                    </Typography>

                                    <Chip
                                      icon={st.icon}
                                      label={st.label}
                                      size="small"
                                      sx={{
                                        background: statusTone.chipBg,
                                        color: statusTone.chipColor,
                                        fontWeight: 700,
                                        "& .MuiChip-icon": {
                                          color: statusTone.chipColor,
                                        },
                                      }}
                                    />
                                  </Stack>

                                  <Stack
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                    useFlexGap
                                    flexWrap="wrap"
                                    sx={{ mt: 0.8, color: "text.secondary" }}
                                  >
                                    <AccessTimeIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                      {formatTime(log.initiated_at)}
                                    </Typography>
                                    {log.duration > 0 && (
                                      <>
                                        <CircleIcon sx={{ fontSize: 4, color: colors.line }} />
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                          {formatDuration(log.duration)}
                                        </Typography>
                                      </>
                                    )}
                                    {log.direction && (
                                      <>
                                        <CircleIcon sx={{ fontSize: 4, color: colors.line }} />
                                        <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                                          {log.direction}
                                        </Typography>
                                      </>
                                    )}
                                    {log.call_type && (
                                      <>
                                        <CircleIcon sx={{ fontSize: 4, color: colors.line }} />
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                          {log.call_type}
                                        </Typography>
                                      </>
                                    )}
                                  </Stack>

                                  {(log.agent_name || log.agent_number || log.call_source_label) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.7, display: "block" }}>
                                      {log.agent_name ? `Agent: ${log.agent_name}` : log.agent_number ? `Agent: ${log.agent_number}` : ""}
                                      {log.call_source_label ? `${log.agent_name || log.agent_number ? " • " : ""}${log.call_source_label}` : ""}
                                    </Typography>
                                  )}

                                  {log.is_blocked && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        mt: 0.7,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.6,
                                        color: theme.palette.error.main,
                                        fontWeight: 700,
                                      }}
                                    >
                                      <BlockIcon sx={{ fontSize: 13 }} />
                                      Blocked for calling
                                    </Typography>
                                  )}
                                </Box>

                                {(log.recording_url || log.recording_file) && (
                                  <Tooltip title="Recording available">
                                    <Avatar
                                      sx={{
                                        width: 34,
                                        height: 34,
                                        background: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
                                        color: theme.palette.primary.main,
                                      }}
                                    >
                                      <PlayCircleIcon sx={{ fontSize: 20 }} />
                                    </Avatar>
                                  </Tooltip>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            background: isDark ? "#141414" : "#fffdf9",
            border: `1px solid ${surface.border}`,
            boxShadow: isDark ? "0 28px 70px rgba(0, 0, 0, 0.5)" : "0 28px 70px rgba(71, 48, 19, 0.18)",
          },
        }}
      >
        {selectedLog && (
          <>
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 2.2, md: 2.8 },
                background: surface.hero,
                borderBottom: `1px solid ${surface.border}`,
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                <Avatar
                  sx={{
                    width: 54,
                    height: 54,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: isDark ? "#15110c" : "#fff",
                  }}
                >
                  <PhoneForwardedIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {selectedLog.called_number || selectedLog.to_number || selectedLog.caller_number || selectedLog.from_number || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.4, color: alpha(surface.textInverse, 0.78) }}>
                    {formatTime(selectedLog.initiated_at)} {selectedLog.direction ? `· ${selectedLog.direction}` : ""}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    label={statusConfig[selectedLog.status]?.label || selectedLog.status}
                    sx={{
                      background: getStatusTone(selectedLog.status).chipBg,
                      color: getStatusTone(selectedLog.status).chipColor,
                      fontWeight: 700,
                    }}
                  />
                  {selectedLog.is_blocked && (
                    <Chip
                      icon={<BlockIcon />}
                      label="Blocked"
                      sx={{
                        background: alpha(theme.palette.error.main, isDark ? 0.22 : 0.12),
                        color: theme.palette.error.main,
                        fontWeight: 700,
                        "& .MuiChip-icon": { color: theme.palette.error.main },
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Box>

            <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
              <Grid container spacing={2}>
                {[
                  {
                    label: "Status",
                    value: statusConfig[selectedLog.status]?.label || selectedLog.status || "-",
                  },
                  {
                    label: "Duration",
                    value: formatDuration(selectedLog.duration),
                  },
                  {
                    label: "Type",
                    value: selectedLog.direction || selectedLog.call_type || "-",
                  },
                ].map((item) => (
                  <Grid item xs={12} sm={4} key={item.label}>
                    <Box
                      sx={{
                        p: 1.75,
                        borderRadius: 3,
                        background: surface.accent,
                        border: `1px solid ${surface.border}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography sx={{ mt: 0.4, fontWeight: 800, fontSize: 18 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background: surface.panelSoft,
                      border: `1px solid ${surface.border}`,
                    }}
                  >
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.4 }}>
                      Call Details
                    </Typography>
                    <Stack spacing={1.1} sx={{ mt: 1 }}>
                      {detailRows.length > 0 ? (
                        detailRows.map((row) => (
                          <Box
                            key={row.label}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              p: 1.1,
                              borderRadius: 2.5,
                              background: isDark ? alpha("#ffffff", 0.03) : "#ffffff",
                              border: `1px solid ${surface.border}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 92, fontWeight: 700 }}
                            >
                              {row.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                flex: 1,
                                fontWeight: 700,
                                fontFamily: "'Courier New', monospace",
                                wordBreak: "break-word",
                              }}
                            >
                              {row.value}
                            </Typography>
                            <IconButton size="small" onClick={() => handleCopy(row.value)}>
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No details available
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background: surface.panelSoft,
                      border: `1px solid ${surface.border}`,
                      height: "100%",
                    }}
                  >
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.4 }}>
                      Call Timeline
                    </Typography>
                    <Stack spacing={1.4} sx={{ mt: 1.2 }}>
                      {timelineRows.length > 0 ? (
                        timelineRows.map((row) => (
                          <Stack key={row.label} direction="row" spacing={1.2} alignItems="center">
                            <Avatar
                              sx={{
                                width: 34,
                                height: 34,
                                background: surface.accent,
                                color: theme.palette.primary.main,
                              }}
                            >
                              {row.icon}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                {row.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTime(row.time)}
                              </Typography>
                            </Box>
                          </Stack>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No timeline data
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background: surface.panelSoft,
                      border: `1px solid ${surface.border}`,
                    }}
                  >
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.4 }}>
                      MyOperator Contact
                    </Typography>
                    {contactLoading ? (
                      <Box sx={{ py: 2, textAlign: "center" }}>
                        <CircularProgress size={22} />
                      </Box>
                    ) : contactInfo?.contacts?.length > 0 ? (
                      <Stack spacing={1.1} sx={{ mt: 1.2 }}>
                        {contactInfo.contacts.slice(0, 1).map((contact, index) => (
                          <Box
                            key={contact.id || contact._id || index}
                            sx={{
                              p: 1.2,
                              borderRadius: 2.5,
                              background: isDark ? alpha("#ffffff", 0.03) : "#ffffff",
                              border: `1px solid ${surface.border}`,
                            }}
                          >
                            <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
                              {contact.name || "Unnamed contact"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                              {contact.phone_number || contact.mobile || getLogNumber(selectedLog)}
                            </Typography>
                            {contact.email_id && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                                {contact.email_id}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.2 }}>
                        No contact found in MyOperator for this number yet.
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {selectedLog.is_blocked && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 1.6,
                        borderRadius: 3,
                        background: alpha(theme.palette.error.main, isDark ? 0.14 : 0.08),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.24)}`,
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: theme.palette.error.main, fontSize: 14 }}>
                        This number is blocked for calling
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                        {selectedLog.blocked_reason || "No reason was added when this number was blocked."}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background: surface.panelSoft,
                      border: `1px solid ${surface.border}`,
                    }}
                  >
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.4 }}>
                      Recording
                    </Typography>
                    <Box sx={{ mt: 1.2 }}>
                      {recordUrl ? (
                        <Button
                          variant="contained"
                          fullWidth
                          href={recordUrl}
                          target="_blank"
                          startIcon={<PlayCircleIcon />}
                          sx={{
                            minHeight: 46,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          }}
                        >
                          Play Recording
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={handleGetRecording}
                          disabled={fetchingRecording}
                          startIcon={fetchingRecording ? <CircularProgress size={18} /> : <PlayCircleIcon />}
                          sx={{ minHeight: 46 }}
                        >
                          {fetchingRecording ? "Fetching..." : "Get Recording"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      background: surface.panelSoft,
                      border: `1px solid ${surface.border}`,
                    }}
                  >
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.4 }}>
                      Notes
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      placeholder="Add notes about this call..."
                      value={notesText}
                      onChange={(e) => setNotesText(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{
                        mt: 1.2,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2.5,
                          background: isDark ? alpha("#ffffff", 0.03) : "#ffffff",
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: 3,
                  background: surface.panelSoft,
                  border: `1px solid ${surface.border}`,
                }}
              >
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.4 }}>
                  Actions
                </Typography>
                <Grid container spacing={1.2} sx={{ mt: 0.3 }}>
                  {[
                    {
                      label: "Call Again",
                      icon: <PhoneIcon />,
                      onClick: () => {
                        const num = getLogNumber(selectedLog);
                        if (num) setPhone(num);
                        setDetailOpen(false);
                      },
                      disabled: selectedLog.is_blocked,
                      background: "linear-gradient(135deg, #1bb56f 0%, #0e8f56 100%)",
                    },
                    {
                      label: "Copy Number",
                      icon: <ContentCopyIcon />,
                      onClick: () => handleCopy(getLogNumber(selectedLog)),
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #ffb347 100%)`,
                    },
                    ...(isSuperAdmin
                      ? [
                          {
                            label: "Add Contact",
                            icon: <PersonAddIcon />,
                            onClick: () => openContactDialog(selectedLog),
                            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, #1a8d58 100%)`,
                          },
                          {
                            label: selectedLog.is_blocked ? "Blocked" : "Block Number",
                            icon: <BlockIcon />,
                            onClick: () => openBlockDialog(selectedLog),
                            disabled: selectedLog.is_blocked,
                            background: "linear-gradient(135deg, #d64f4f 0%, #a62727 100%)",
                          },
                        ]
                      : []),
                  ].map((action) => (
                    <Grid item xs={12} sm={6} key={action.label}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={action.icon}
                        onClick={action.onClick}
                        disabled={action.disabled}
                        sx={{
                          minHeight: 44,
                          background: action.background,
                          color: action.disabled ? theme.palette.text.secondary : "#fff",
                          "&.Mui-disabled": {
                            background: action.background,
                            color: theme.palette.text.secondary,
                          },
                        }}
                      >
                        {action.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>

            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 2.5 }, pt: 0 }}>
              <Button
                onClick={() => setDetailOpen(false)}
                variant="contained"
                sx={{
                  minWidth: 120,
                  background: isDark
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.92)} 0%, ${alpha("#8d5700", 0.95)} 100%)`
                    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #ffb347 100%)`,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={contactDialogOpen}
        onClose={() => !savingContact && setContactDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: isDark ? "#141414" : "#fffdf9",
            border: `1px solid ${surface.border}`,
          },
        }}
      >
        <DialogContent sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Add Contact
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Save this caller inside MyOperator contacts for future calling workflows.
          </Typography>

          <Stack spacing={1.5}>
            <TextField
              label="Phone Number"
              value={contactForm.phone_number}
              onChange={(e) => setContactForm((prev) => ({ ...prev, phone_number: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Name"
              value={contactForm.name}
              onChange={(e) => setContactForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              value={contactForm.email_id}
              onChange={(e) => setContactForm((prev) => ({ ...prev, email_id: e.target.value }))}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button onClick={() => setContactDialogOpen(false)} disabled={savingContact}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateContact}
            disabled={!contactForm.phone_number || savingContact}
            startIcon={savingContact ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
          >
            {savingContact ? "Saving..." : "Save Contact"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={blockDialogOpen}
        onClose={() => !savingBlock && setBlockDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: isDark ? "#141414" : "#fffdf9",
            border: `1px solid ${surface.border}`,
          },
        }}
      >
        <DialogContent sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Block Number
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            This will stop future call initiation from the admin call center for this number.
          </Typography>

          <Stack spacing={1.5}>
            <TextField
              label="Phone Number"
              value={blockForm.phone_number}
              onChange={(e) => setBlockForm((prev) => ({ ...prev, phone_number: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Reason"
              value={blockForm.reason}
              onChange={(e) => setBlockForm((prev) => ({ ...prev, reason: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Notes"
              value={blockForm.notes}
              onChange={(e) => setBlockForm((prev) => ({ ...prev, notes: e.target.value }))}
              fullWidth
              size="small"
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button onClick={() => setBlockDialogOpen(false)} disabled={savingBlock}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBlockNumber}
            disabled={!blockForm.phone_number || savingBlock}
            startIcon={savingBlock ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
          >
            {savingBlock ? "Blocking..." : "Block Number"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contactsDialogOpen}
        onClose={() => setContactsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: isDark ? "#141414" : "#fffdf9",
            border: `1px solid ${surface.border}`,
          },
        }}
      >
        <DialogContent sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            MyOperator Contacts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            These contacts are fetched live from MyOperator, and synced call logs can be rebuilt from MyOperator whenever local history needs to be restored.
          </Typography>

          {contactsLoading ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : contactsList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No contacts returned from MyOperator.
            </Typography>
          ) : (
            <Stack spacing={1.1}>
              {contactsList.map((contact, index) => (
                <Box
                  key={contact.id || contact._id || `${contact.phone_number}-${index}`}
                  sx={{
                    p: 1.3,
                    borderRadius: 2.5,
                    background: surface.panelSoft,
                    border: `1px solid ${surface.border}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
                    {contact.name || "Unnamed contact"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                    {contact.phone_number || contact.mobile || "-"}
                  </Typography>
                  {contact.email_id && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                      {contact.email_id}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5 }}>
          <Button onClick={() => setContactsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CallLogs;
